/**
 * SASADM-0170 (saas-auth SASATH-0387 §5.1) — the banner for a write that committed but whose
 * routing deploy was REJECTED or FAILED. Never a toast: the operator has to act (FEED-004).
 * The status word is a text chip, not just colour (A11Y-005).
 *
 * SASADM-0176 (SASATH-0391) — the 0170 failure banner, renamed; a PARTIAL deploy (applied, but
 * some tenants / products held back on their previous routing) is a warning naming and linking
 * each held-back entry (FEED-005), with a first line when the current tenant is among them.
 */
import { Alert, AlertTitle, Box, Chip, IconButton, Link, Stack, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import type { RoutingDeployOutcome } from '@/api/types';
import { useCopy } from '@/hooks/useCopy';

export interface RoutingDeployBannerProps {
  /** PARTIAL, REJECTED or FAILED. */
  outcome: RoutingDeployOutcome;
  /** E.g. a "Deploy this tenant's routing" button, or a Link. */
  action?: ReactNode;
  onClose: () => void;
  /** The tenant screen's tenant: a PARTIAL that held it back says so first. */
  currentTenantId?: number;
}

export function RoutingDeployBanner({
  outcome,
  action,
  onClose,
  currentTenantId,
}: RoutingDeployBannerProps) {
  const { t } = useCopy();
  const partial = outcome.status === 'PARTIAL';
  const heldBack = outcome.heldBack ?? [];
  const thisTenantHeld =
    currentTenantId != null &&
    heldBack.some((h) => h.kind === 'TENANT' && h.id === currentTenantId);
  return (
    <Alert
      severity={partial ? 'warning' : 'error'}
      sx={{ mb: 2 }}
      // MUI renders `action` INSTEAD of the built-in close button, so the × is added here.
      action={
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
          {action}
          <Tooltip title={t('common.close', 'Close')}>
            <IconButton
              size="small"
              color="inherit"
              aria-label={t('common.close', 'Close')}
              onClick={onClose}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      }
    >
      <AlertTitle>
        {partial
          ? t(
              'routingDeploy.partialTitle',
              'Routing deployed, but some settings were kept on their previous version',
            )
          : t('routingDeploy.failedTitle', "Saved, but the routing deploy didn't apply")}
        <Chip size="small" variant="outlined" label={outcome.status} sx={{ ml: 1 }} />
      </AlertTitle>
      {partial ? (
        <>
          {thisTenantHeld && (
            <Box component="span" sx={{ display: 'block', fontWeight: 'bold', mb: 0.5 }}>
              {t('routingDeploy.partialThisTenant', "Your change to this tenant didn't apply.")}
            </Box>
          )}
          {heldBack.length === 0
            ? // A tolerant read: PARTIAL without its list shows the server sentence.
              (outcome.message ??
              t(
                'routingDeploy.partialTitle',
                'Routing deployed, but some settings were kept on their previous version',
              ))
            : t(
                'routingDeploy.partialBody',
                "These were kept on their previous version because their settings couldn't be applied:",
              )}{' '}
          {heldBack.map((h, i) => (
            <Box component="span" key={`${h.kind}:${h.id}`}>
              {i > 0 && ', '}
              <Link
                component={RouterLink}
                to={h.kind === 'TENANT' ? `/tenants/${h.id}` : `/reference/products/${h.id}`}
                color="inherit"
              >
                {h.slug}
              </Link>
            </Box>
          ))}
          <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
            {t('routingDeploy.partialHelp', 'Fix their settings, then deploy again.')}
          </Box>
        </>
      ) : (
        <>
          {outcome.message ??
            t('routingDeploy.failedNoMessage', 'The edge did not accept the new routing.')}
          <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
            {t(
              'routingDeploy.failedHelp',
              'The change is saved. Sites keep serving the last routing that applied. Fix the cause, then deploy routing again.',
            )}
          </Box>
        </>
      )}
    </Alert>
  );
}
